import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroller";
import { useNavigate, useLocation } from "react-router-dom";

import ThemePreferences from "./ThemePreferences";
import ThemeItem from "./ThemeItem";
import ThemeFetching from "fetching/ThemeFetching";
import NotFound from "notFound/NotFound";
import {
  setCurrentNavPage,
  clearSourcesTokens,
  setThemesPaginationPage,
  setThemeType,
  setThemeScope,
} from "actions";
import { fetchTemplates } from "thunks";
import { TEMPLATE_TYPES } from "constants";
import _ from "i18n";
import PageHeader from "common/PageHeader";
import { STEPS } from "Common";
import ThemePreview from "../editor/ThemePreview";
import ThemeListFilter from "./filter/ThemeListFilter";
import TTPTab from "../ui/tabs/TTPTab";
import { initTemplatesList } from "../../actions/actions";
import { COMMUNITY_SCOPE } from "../../constants";

const ThemesListPage = ({ isUAVersion = false } = {}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { themes, nbResult, fetching, filter, _requests, step, content } =
    useSelector((state) => ({
      themes: state.themes.list.items,
      nbResult: state.themes.list.nbResult,
      fetching: state.themes.list.fetching,
      filter: state.themes.filter,
      _requests: state.params.sourcesTokens,
      step: state.params.step,
      content: state.themes.content,
    }));

  const [selectedItemsIds, setSelectedItemsIds] = useState([]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [currentTab, setCurrentTab] = useState("STATIC");
  const scrollParentRef = useRef(null);
  const isEmbeddedInUa = location.pathname.includes("/newsletter-ua");

  useEffect(() => {
    dispatch(setCurrentNavPage("TEMPLATES"));
    dispatch(initTemplatesList());
    dispatch(setThemeScope(COMMUNITY_SCOPE));
    dispatch(setThemeType(TEMPLATE_TYPES["DRAG_AND_DROP"]));

    return () => {
      _requests.forEach((req) =>
        req.sourceToken.cancel("Operation canceled by the user."),
      );
      dispatch(clearSourcesTokens());
    };
  }, [dispatch]);

  useEffect(() => {
    setHasMoreItems(!fetching);
  }, [fetching]);

  useEffect(() => {
    if (filter) {
      const newFilter = { ...filter, getUserAvatar: true, isBlock: true };
      dispatch(fetchTemplates(newFilter));
    }
  }, [
    filter.language,
    filter.application,
    filter.visibility,
    filter.searchWord,
    filter.sortDirection,
    filter.scope,
    filter.category,
    filter.type,
    filter.paginationPage,
    dispatch,
  ]);

  const openSettingsModal = () => setShowSettingsModal(true);

  const closeSettingsModal = () => {
    const items = document
      .getElementById("template-page")
      .getElementsByClassName("template-checkbox");
    for (let i = 0; i < items.length; i++) {
      items[i].checked = false;
    }
    setShowSettingsModal(false);
    setSelectedItemsIds([]);
  };

  const showOneThemeSettings = (themeId) => {
    setShowSettingsModal(true);
    setSelectedItemsIds([themeId]);
  };

  const handleItemChange = (e) => {
    const { value, checked } = e.target;
    setSelectedItemsIds((prevIds) => {
      if (checked) {
        return [...prevIds, value];
      } else {
        return prevIds.filter((item) => item !== value);
      }
    });
  };

  const loadItems = () => {
    if (!fetching && themes.length < nbResult) {
      const currentPage = filter.paginationPage;
      setHasMoreItems(false);
      dispatch(setThemesPaginationPage(currentPage + 1));
    }
  };

  const createTemplate = (type) => {
    navigate("/drag-drop", { state: { type, from: location.pathname } });
  };

  const renderThemes = () => {
    let items = [];
    if (themes && themes.length > 0) {
      items = themes.map((theme) => (
        <ThemeItem
          key={`theme-${theme.id}`}
          {...theme}
          onSettingClick={() => showOneThemeSettings(theme.id)}
          onItemChange={handleItemChange}
        />
      ));
    }

    if (fetching) {
      for (let index = 0; index < 5; index++) {
        items.push(<ThemeFetching key={`theme-fetching-${index}`} />);
      }
    }

    return items.length > 0 ? (
      <InfiniteScroll
        className={`template-list row`}
        style={{ maxWidth: "75rem" }}
        pageStart={0}
        loadMore={loadItems}
        hasMore={hasMoreItems}
        useWindow={isUAVersion ? false : true}
        getScrollParent={() =>
          isUAVersion ? document.querySelector(".newsletter-ua-page") : null
        }
      >
        {items}
      </InfiniteScroll>
    ) : (
      <NotFound mainClass={"small-10"} />
    );
  };

  const switchCurrentTab = (tabId) => {
    setCurrentTab(tabId);
    const currentTab = THEME_TYPE_TABS.find((tab) => tab.id === tabId);
    dispatch(setThemeType(currentTab.type));
  };

  return (
    <div id="template-page" ref={scrollParentRef}>
      <PageHeader {...PAGE_HEADER}>
        <button
          className="btn secondary text-uppercase"
          onClick={() => createTemplate("DRAG_AND_DROP")}
        >
          <img src="/img/icons/add.svg" />
          {_("create")}
        </button>
      </PageHeader>
      <div className={`ttp-tab-container`}>
        <TTPTab
          tabs={THEME_TYPE_TABS}
          activeTab={currentTab}
          selectTab={switchCurrentTab}
        />
        <ThemeListFilter />
        {renderThemes()}
      </div>
      {showSettingsModal && (
        <ThemePreferences
          themesIds={selectedItemsIds}
          onCloseModal={closeSettingsModal}
        />
      )}
      {step === STEPS["THEME_PREVIEW"] && (
        <ThemePreview
          dispatch={dispatch}
          content={content}
          previous={{
            step: STEPS["THEMES_LIST"],
            url: isEmbeddedInUa ? "" : "/templates",
          }}
        />
      )}
    </div>
  );
};

export default ThemesListPage;

const THEME_TYPE_TABS = [
  { id: "STATIC", label: "emailing", type: ["DRAG_AND_DROP"] },
  { id: "DYNAMIC", label: "newsletter", type: ["TEMPLATE"] },
  { id: "TEXT", label: "Rich Text", type: ["SIMPLE", "WRAPPER"] },
];

const PAGE_HEADER = {
  header: "models",
  subHeader: "Manage email models and newsletters that are ready to be sent .",
  icon: "TEMPLATES",
  action: "drag-drop",
};
